import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  setDoc,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { fetchSignInMethodsForEmail, createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateUniqueIdentificationCode } from '../utils/identificationCode';
import { hasKidsServicesFromCodes } from '../utils/kidsServices';
import { resizeImageFromDataUrl } from '../utils/imageResize';
import { normalizeLocationFromAutocomplete } from '../utils/locationNormalization';
import { useAuth } from '../contexts/AuthContext';
import { useSignup } from '../contexts/SignupContext';
import { updateUserType } from '../utils/userType';
import { logSignupError, logProfileCreationError } from '../utils/errorLogger';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import SearchableCountrySelect from '../components/SearchableCountrySelect';
import { useTranslation } from '../hooks/useTranslation';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';
import phoneIcon from '../assets/images/telephone_logo.png';
import styles from './SignupPage.module.css';
import ProgressBar from '../components/ProgressBar';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { phoneCountryOptions } from '../utils/phoneCountryCodes';

const initialFormState = {
  name: '',
  mainCity: '',
  country: null, // Code pays depuis l'autocomplete (voir config/countries.js)
  city: '',
  department: null,
  additionalCities: [],
  canTravel: false,
  instagram: '',
  // Codes pays pour WhatsApp et téléphone (par défaut +32)
  whatsappCountryCode: '+32',
  whatsapp: '',
  phoneCountryCode: '+32',
  phone: '',
  tiktok: '',
  externalLink: '',
  primaryContact: '', // Réseau social primaire
  profilePhoto: '',
  profilePhotoFile: null,
  galleryPhotos: [],
  galleryPhotoFiles: [],
  description: '',
  services: [],
  coordinates: null,
};

const initialAccountState = {
  displayName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

// contactFields sera créé dynamiquement dans le composant pour utiliser les traductions

function SignupPage() {
  const navigate = useNavigate();
  const { type } = useParams();
  const providerType = type || 'hair';
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { t, language } = useTranslation();
  
  // Créer les champs de contact avec traductions
  const contactFields = useMemo(() => [
    {
      key: 'instagram',
      label: t('signup.contactFields.instagram'),
      placeholder: t('signup.contactFields.instagramPlaceholder'),
      type: 'text',
      icon: instagramIcon,
    },
    {
      key: 'tiktok',
      label: t('signup.contactFields.tiktok'),
      placeholder: t('signup.contactFields.tiktokPlaceholder'),
      type: 'text',
      icon: tiktokIcon,
    },
    {
      key: 'whatsapp',
      label: t('signup.contactFields.whatsapp'),
      placeholder: t('signup.contactFields.whatsappPlaceholder'),
      type: 'tel',
      icon: whatsappIcon,
    },
    {
      key: 'phone',
      label: t('signup.contactFields.phone'),
      placeholder: t('signup.contactFields.phonePlaceholder'),
      type: 'tel',
      icon: phoneIcon,
    },
    {
      key: 'externalLink',
      label: t('signup.contactFields.externalLink'),
      placeholder: t('signup.contactFields.externalLinkPlaceholder'),
      type: 'url',
      icon: linkIcon,
    },
  ], [t]);
  
  const [formData, setFormData] = useState(initialFormState);
  const [alreadyHairstylist, setAlreadyHairstylist] = useState(false);
  const [existingHairstylistId, setExistingHairstylistId] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  
  useEffect(() => {
    const checkExistingProfile = async () => {
      // Ne pas vérifier si l'utilisateur vient de la page de paiement ou de confirmation
      // (pour éviter d'afficher le message "Tu as déjà un profil" juste après la création)
      if (location.state?.fromPayment || location.state?.fromSubscriptionSuccess) {
        setAlreadyHairstylist(false);
        setExistingHairstylistId(null);
        return;
      }
      
      if (!currentUser) {
        setAlreadyHairstylist(false);
        setExistingHairstylistId(null);
        return;
      }
      try {
        const q = query(
          collection(db, 'hairstylists'),
          where('userId', '==', currentUser.uid),
          limit(1),
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setAlreadyHairstylist(true);
          setExistingHairstylistId(snapshot.docs[0].id);
        } else {
          setAlreadyHairstylist(false);
          setExistingHairstylistId(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to verify hairstylist profile', error);
      }
    };

    checkExistingProfile();
  }, [currentUser, location.state]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [googlePlacesLoaded, setGooglePlacesLoaded] = useState(false);
  const urlStep = searchParams.get('step');
  const [step, setStep] = useState(urlStep === '2' ? 2 : 1); // Étape 1: formulaire profil, Étape 2: création compte
  const [accountData, setAccountData] = useState(initialAccountState);
  const requiresAccountCreation = !currentUser;
  const { setFormData: setSignupFormData, setAccountData: setSignupAccountData, setRequiresAccountCreation: setSignupRequiresAccountCreation, setProfileId, signupFormData } = useSignup();
  
  // Gérer les changements d'URL (seulement si on est sur la page signup)
  useEffect(() => {
    // Ne rien faire si on n'est plus sur la page signup
    if (!location.pathname.includes('/signup')) {
      return;
    }
    
    const urlStep = searchParams.get('step');
    if (urlStep === '2') {
      setStep(2);
    } else {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.pathname]); // Ne PAS inclure 'step' pour éviter la boucle de re-renders

  // Restaurer les données depuis sessionStorage et Context au chargement
  useEffect(() => {
    const tempDataStr = sessionStorage.getItem('tempHairstylistProfile');
    
    // Si les images sont dans le Context, les utiliser en priorité
    if (signupFormData && tempDataStr) {
      try {
        const tempData = JSON.parse(tempDataStr);
        
        // Si on est à l'étape 1, restaurer les métadonnées depuis sessionStorage
        // et fusionner avec les images du Context
        if (step === 1 && tempData.formData && tempData.imagesInContext) {
          setFormData({
            ...signupFormData, // Les images sont ici !
            ...tempData.formData, // Fusionner les métadonnées
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring data from Context:', error);
      }
    } else if (tempDataStr) {
      // Pas de Context, restaurer depuis sessionStorage uniquement
      try {
        const tempData = JSON.parse(tempDataStr);
        
        // Si on est à l'étape 1, restaurer formData
        if (step === 1 && tempData.formData) {
          setFormData((prev) => ({
            ...prev,
            ...tempData.formData,
            // Garder les images du state React si elles existent
            profilePhoto: prev.profilePhoto || tempData.formData.profilePhoto || '',
            galleryPhotos: prev.galleryPhotos.length > 0 ? prev.galleryPhotos : (tempData.formData.galleryPhotos || []),
          }));
        }
        
        // Si on est à l'étape 2, restaurer accountData
        if (step === 2 && tempData.accountData) {
          setAccountData(tempData.accountData);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring data from sessionStorage:', error);
      }
    }
  }, [step, signupFormData]); // Ajouter signupFormData aux dépendances


  const showAccountStep = step === 2 && (requiresAccountCreation || submitting || isNavigating);
  const locationInputRef = useRef(null);
  const mainCityInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mainCityAutocompleteRef = useRef(null);
  const formRef = useRef(null);

  // Ne plus demander d'authentification, on crée le compte à l'étape 2
  // useEffect supprimé

  useEffect(() => {
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [categorySnap, serviceSnap] = await Promise.all([
          getDocs(query(collection(db, 'categories'), where('type', '==', providerType))),
          getDocs(query(collection(db, 'services'), where('type', '==', providerType))),
        ]);

        setCategories(
          categorySnap.docs
            .map((doc) => doc.data())
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
        setServices(
          serviceSnap.docs
            .map((doc) => {
              const data = doc.data();
              // Normaliser les noms de champs: supporte prestationCode/prestation ET code/label
              return {
                ...data,
                code: data.code || data.prestationCode || '',
                label: data.label || data.prestation || '',
              };
            })
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load catalog', error);
        setFeedback({ type: 'error', message: t('signup.errorLoadCategories') });
      } finally {
        setLoadingCatalog(false);
      }
    };

    fetchCatalog();
  }, [providerType]);

  // Réinitialiser les prestations sélectionnées quand on change de type (ex. modal → ongles)
  useEffect(() => {
    setFormData((prev) => (prev.services?.length ? { ...prev, services: [] } : prev));
  }, [providerType]);

  useEffect(() => {
    if (window.google?.maps?.places) {
      setGooglePlacesLoaded(true);
      return () => {};
    }

    let script = document.querySelector('script[data-google-places]');
    const handleLoad = () => setGooglePlacesLoaded(true);
    const handleError = () => {
      // eslint-disable-next-line no-console
      console.error('Impossible de charger Google Places pour la recherche de villes.');
    };

    loadGoogleMaps({ requirePlaces: true, language: language || 'fr' })
      .then(handleLoad)
      .catch(handleError);
  }, [language]);

  const addAdditionalCity = useCallback((city) => {
    if (!city) return;
    setFormData((prev) => {
      const set = new Set(prev.additionalCities);
      set.add(city);
      return { ...prev, additionalCities: Array.from(set) };
    });
  }, []);

  const handleRemoveAdditionalCity = (city) => {
    setFormData((prev) => ({
      ...prev,
      additionalCities: prev.additionalCities.filter((c) => c !== city),
    }));
  };

  useEffect(() => {
    if (!googlePlacesLoaded || !formData.canTravel || !locationInputRef.current) {
      return () => {};
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(locationInputRef.current, {
      types: ['geocode'],
      // Pas de componentRestrictions : Google limite à 5 pays ; on valide le pays côté app (config/countries)
    });

    const handlePlaceChanged = () => {
      const place = autocompleteRef.current?.getPlace();
      if (!place) return;
      
      // Utiliser la fonction de normalisation pour éviter les duplications
      const normalized = normalizeLocationFromAutocomplete(place);
      const label = normalized.city || normalized.mainCity || place.name || locationInputRef.current?.value || '';
      
      if (label.trim()) {
        addAdditionalCity(label.trim());
      }
      
      if (locationInputRef.current) {
        // eslint-disable-next-line no-param-reassign
        locationInputRef.current.value = '';
      }
    };

    autocompleteRef.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [addAdditionalCity, formData.canTravel, googlePlacesLoaded]);

  useEffect(() => {
    if (!googlePlacesLoaded || !mainCityInputRef.current) {
      return () => {};
    }

    const opts = { types: ['geocode'] };
    if (formData.country) {
      opts.componentRestrictions = { country: [formData.country.toLowerCase()] };
    }
    mainCityAutocompleteRef.current = new window.google.maps.places.Autocomplete(mainCityInputRef.current, opts);

    const handlePlaceChanged = () => {
      const place = mainCityAutocompleteRef.current?.getPlace();
      if (!place) return;

      // [SIGNUP DEBUG] Réponse Google Places (ville principale)
      const hasAddressComponents = !!(place.address_components && place.address_components.length);
      const countryFromPlace = place.address_components?.find((c) => c.types.includes('country'))?.short_name;
      // eslint-disable-next-line no-console
      console.log('[SignupPage] place_changed (ville principale)', {
        placeName: place.name,
        hasAddressComponents,
        countryFromPlace,
        addressComponentsCount: place.address_components?.length ?? 0,
      });

      const normalized = normalizeLocationFromAutocomplete(place);
      const mainCityValue = normalized.city || normalized.mainCity || '';

      // [SIGNUP DEBUG] Résultat normalisation
      // eslint-disable-next-line no-console
      console.log('[SignupPage] normalized (ville principale)', {
        country: normalized.country,
        city: normalized.city,
        mainCity: normalized.mainCity,
        department: normalized.department,
      });

      if (!normalized.country) {
        setFeedback({
          type: 'error',
          message: t('signup.errorCountryNotSupported'),
        });
        if (mainCityInputRef.current) mainCityInputRef.current.value = '';
        return;
      }
      setFeedback(null);

      // Coordonnées pour la carte (place.geometry peut être absent si pas demandé explicitement)
      let coordinates = null;
      if (place.geometry?.location) {
        const loc = place.geometry.location;
        coordinates = typeof loc.lat === 'function'
          ? { lat: loc.lat(), lng: loc.lng() }
          : { lat: loc.lat, lng: loc.lng };
      }

      setFormData((prev) => ({
        ...prev,
        mainCity: mainCityValue,
        country: normalized.country || prev.country,
        city: normalized.city || mainCityValue,
        department: normalized.department ?? prev.department,
        coordinates: coordinates || prev.coordinates,
      }));
    };

    mainCityAutocompleteRef.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (mainCityAutocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(mainCityAutocompleteRef.current);
        mainCityAutocompleteRef.current = null;
      }
    };
  }, [googlePlacesLoaded, formData.country]);

  // Synchroniser les indicatifs téléphone et WhatsApp avec le pays sélectionné uniquement si aucun numéro n'est encodé
  useEffect(() => {
    if (!formData.country) return;
    const opt = phoneCountryOptions.find((o) => o.code === formData.country.toUpperCase());
    if (!opt) return;
    const phoneTrimmed = (formData.phone || '').trim();
    const whatsappTrimmed = (formData.whatsapp || '').trim();
    if (phoneTrimmed.length > 0 && whatsappTrimmed.length > 0) return;
    setFormData((prev) => {
      const prevPhone = (prev.phone || '').trim();
      const prevWhatsapp = (prev.whatsapp || '').trim();
      const updates = {};
      if (prevPhone.length === 0 && prev.phoneCountryCode !== opt.dialCode) {
        updates.phoneCountryCode = opt.dialCode;
      }
      if (prevWhatsapp.length === 0 && prev.whatsappCountryCode !== opt.dialCode) {
        updates.whatsappCountryCode = opt.dialCode;
      }
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [formData.country, formData.phone, formData.whatsapp]);

  const servicesByCategory = useMemo(() => {
    return categories.map((category) => {
      // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
      let categoryLabel = category.label || '';
      if (language && language !== 'fr' && category.en) {
        categoryLabel = category.en;
      }
      
      const categoryServices = services
        .filter((service) => service.categoryCode === category.categoryCode)
        .map((service) => {
          // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
          let serviceLabel = service.label || service.prestation || '';
          if (language && language !== 'fr' && service.en) {
            serviceLabel = service.en;
          }
          return {
            ...service,
            label: serviceLabel,
          };
        });
      
      return {
        ...category,
        label: categoryLabel,
        services: categoryServices,
      };
    });
  }, [categories, services, language]);

  useEffect(() => {
    if (servicesByCategory.length === 0 || servicesByCategory.length > 3) {
      return;
    }

    const categoryKeys = servicesByCategory.map((category) => category.categoryCode);
    setExpanded((prev) => {
      const prevKeys = Object.keys(prev);
      const sameSet =
        prevKeys.length > 0 && prevKeys.every((key) => categoryKeys.includes(key));
      if (sameSet) {
        return prev;
      }
      const next = {};
      categoryKeys.forEach((key) => {
        next[key] = true;
      });
      return next;
    });
  }, [servicesByCategory]);

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // Pour Instagram et TikTok : lowercase uniquement, pas d'espaces, caractères valides pour username
    if (field === 'instagram' || field === 'tiktok') {
      // Convertir en lowercase et supprimer les espaces
      value = value.toLowerCase().replace(/\s/g, '');
      // Garder uniquement les caractères valides pour un username (lettres, chiffres, underscore, point)
      value = value.replace(/[^a-z0-9._]/g, '');
      // Supprimer le @ au début si l'utilisateur l'a tapé (on l'ajoutera à la sauvegarde)
      if (value.startsWith('@')) {
        value = value.slice(1);
      }
    }
    
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Si un champ de contact est modifié, sélectionner automatiquement le premier réseau renseigné
      // comme réseau primaire si aucun n'est sélectionné
      if (['instagram', 'whatsapp', 'tiktok', 'externalLink'].includes(field)) {
        const filledContacts = contactFields.filter(
          (f) => (f.key === field ? value : updated[f.key])?.trim()
        );
        
        // Si aucun réseau primaire n'est sélectionné et qu'on a au moins un contact rempli
        if (!updated.primaryContact && filledContacts.length > 0) {
          // Sélectionner le premier contact rempli
          updated.primaryContact = filledContacts[0].key;
        }
        
        // Si on vide le réseau primaire actuellement sélectionné, sélectionner un autre
        if (updated.primaryContact === field && !value?.trim() && filledContacts.length > 0) {
          updated.primaryContact = filledContacts[0].key;
        }
      }
      
      return updated;
    });
  };

  const handleCountryCodeChange = (field) => (newCode) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newCode,
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleServiceToggle = (code) => {
    setFormData((prev) => {
      const set = new Set(prev.services);
      if (set.has(code)) {
        set.delete(code);
      } else {
        set.add(code);
      }
      return { ...prev, services: Array.from(set) };
    });
  };

  const handleCategoryToggle = (categoryCode) => {
    const category = servicesByCategory.find(cat => cat.categoryCode === categoryCode);
    if (!category) return;

    const categoryServiceCodes = category.services.map(s => s.code);
    const allSelected = categoryServiceCodes.every(code => formData.services.includes(code));

    setFormData((prev) => {
      const set = new Set(prev.services);
      if (allSelected) {
        // Désélectionner tous les services de cette catégorie
        categoryServiceCodes.forEach(code => set.delete(code));
      } else {
        // Sélectionner tous les services de cette catégorie
        categoryServiceCodes.forEach(code => set.add(code));
      }
      return { ...prev, services: Array.from(set) };
    });
  };

  const handleProfilePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

     // N'accepter que des images
    if (!file.type || !file.type.startsWith('image/')) {
      alert(t('signup.errorImagesOnlyProfile'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        profilePhoto: reader.result,
        profilePhotoFile: file,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    // Filtrer pour ne garder que les images
    const imageFiles = files.filter((file) => file.type && file.type.startsWith('image/'));
    if (!imageFiles.length) {
      alert(t('signup.errorImagesOnlyGallery'));
      return;
    }

    const newFiles = imageFiles.slice(0, 6 - formData.galleryPhotos.length);
    const newPreviews = [];
    let loadedCount = 0;

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        loadedCount += 1;
        if (loadedCount === newFiles.length) {
          setFormData((prev) => ({
            ...prev,
            galleryPhotos: [...prev.galleryPhotos, ...newPreviews].slice(0, 6),
            galleryPhotoFiles: [...prev.galleryPhotoFiles, ...newFiles].slice(0, 6),
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleGalleryRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      galleryPhotos: prev.galleryPhotos.filter((_, i) => i !== index),
      galleryPhotoFiles: prev.galleryPhotoFiles.filter((_, i) => i !== index),
    }));
  };

  const toggleCategory = (code) => {
    setExpanded((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const handleAdditionalCityKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const { value } = event.target;
    if (!value.trim()) return;
    addAdditionalCity(value.trim());
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  // Fonction pour créer un profil pour un utilisateur existant
  const createProfileForExistingUser = async (profileFormData, user) => {
    // 1. Générer le code d'identification
    const identificationCode = await generateUniqueIdentificationCode();

    // 2. Upload de la photo de profil
    let profilePhotoURL = '';
    if (profileFormData.profilePhoto && profileFormData.profilePhoto.startsWith('data:')) {
      const response = await fetch(profileFormData.profilePhoto);
      const blob = await response.blob();
      const timestamp = Date.now();
      const profilePhotoRef = ref(
        storage,
        `hairstylists/${profileFormData.name.trim().replace(/\s+/g, '_')}_${timestamp}/profile.jpg`
      );
      await uploadBytes(profilePhotoRef, blob);
      profilePhotoURL = await getDownloadURL(profilePhotoRef);
    }

    // 3. Upload des photos de galerie (redimensionnées pour optimiser le chargement)
    const galleryPhotoURLs = [];
    if (profileFormData.galleryPhotos && profileFormData.galleryPhotos.length > 0) {
      const timestamp = Date.now();
      const uploadPromises = profileFormData.galleryPhotos
        .filter(photo => photo && photo.startsWith('data:'))
        .map(async (photoDataUrl, index) => {
          try {
            // Redimensionner l'image à 400x400px max pour optimiser le chargement dans les vignettes
            const resizedDataUrl = await resizeImageFromDataUrl(photoDataUrl, 400, 400, 0.85);
            const response = await fetch(resizedDataUrl);
            const blob = await response.blob();
            const galleryPhotoRef = ref(
              storage,
              `hairstylists/${profileFormData.name.trim().replace(/\s+/g, '_')}_${timestamp}/gallery_${index}.jpg`
            );
            await uploadBytes(galleryPhotoRef, blob);
            return getDownloadURL(galleryPhotoRef);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Erreur upload photo galerie ${index}:`, error);
            return null;
          }
        });
      const urls = await Promise.all(uploadPromises);
      galleryPhotoURLs.push(...urls.filter(Boolean));
    }

    // 4. Vérifier les services enfants
    const kidsServiceCodeSet = new Set(['ENF001', 'ENF002', 'ENF003', 'ENF004', 'ENF005']);
    const kidsAccepted = hasKidsServicesFromCodes(profileFormData.services, kidsServiceCodeSet);

    // 5. Normaliser les numéros de téléphone / WhatsApp
    const buildPhoneValue = (countryCode, number) => {
      const raw = (number || '').trim();
      if (!raw) return '';
      if (raw.startsWith('+')) {
        return raw.replace(/\s+/g, '');
      }
      let digits = raw.replace(/[^0-9]/g, '');
      if (digits.startsWith('0')) {
        digits = digits.slice(1);
      }
      const code = (countryCode || '+32').trim();
      return `${code}${digits}`;
    };

    const normalizedWhatsapp = buildPhoneValue(profileFormData.whatsappCountryCode, profileFormData.whatsapp);
    const normalizedPhone = buildPhoneValue(profileFormData.phoneCountryCode, profileFormData.phone);

    // [SIGNUP DEBUG] profileFormData (createProfileForExistingUser)
    // eslint-disable-next-line no-console
    console.log('[SignupPage] profileFormData (existing user)', {
      country: profileFormData.country,
      city: profileFormData.city,
      mainCity: profileFormData.mainCity,
      department: profileFormData.department,
    });

    // 6. Créer le profil hairstylist FREE
    const payload = {
      name: profileFormData.name.trim(),
      mainCity: profileFormData.mainCity.trim(),
      cities: [profileFormData.mainCity.trim(), ...(profileFormData.additionalCities || [])].filter(Boolean),
      country: profileFormData.country || null,
      city: profileFormData.city?.trim() || profileFormData.mainCity.trim() || null,
      department: profileFormData.department ?? null,
      description: profileFormData.description.trim(),
      canTravel: profileFormData.canTravel,
      kidsAccepted,
      services: profileFormData.services,
      profilePhoto: profilePhotoURL,
      galleryPhotos: galleryPhotoURLs,
      contact: {
        instagram: profileFormData.instagram?.trim() ? `@${profileFormData.instagram.trim().replace(/^@/, '')}` : '',
        whatsapp: normalizedWhatsapp,
        phone: normalizedPhone,
        tiktok: profileFormData.tiktok?.trim() ? `@${profileFormData.tiktok.trim().replace(/^@/, '')}` : '',
        externalLink: profileFormData.externalLink?.trim() || '',
        primaryContact: profileFormData.primaryContact?.trim() || '',
      },
      instagram: profileFormData.instagram?.trim() ? `@${profileFormData.instagram.trim().replace(/^@/, '')}` : '',
      whatsapp: normalizedWhatsapp,
      phone: normalizedPhone,
      tiktok: profileFormData.tiktok?.trim() ? `@${profileFormData.tiktok.trim().replace(/^@/, '')}` : '',
      externalLink: profileFormData.externalLink?.trim() || '',
      primaryContact: profileFormData.primaryContact?.trim() || '',
      coordinates: profileFormData.coordinates,
      identificationCode,
      userId: user.uid,
      userEmail: user.email,
      subscriptionActive: false,
      subscriptionStartDate: null,
      accountType: 'FREE',
      type: providerType, // Type de prestataire
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // [SIGNUP DEBUG] payload Firestore (createProfileForExistingUser)
    // eslint-disable-next-line no-console
    console.log('[SignupPage] payload Firestore (existing user)', {
      country: payload.country,
      city: payload.city,
      mainCity: payload.mainCity,
      department: payload.department,
    });

    const docRef = await addDoc(collection(db, 'hairstylists'), payload);

    return docRef.id;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    // Étape 1 : Validation du formulaire profil
    if (step === 1) {
    const missing = [];
    if (!formData.name.trim()) missing.push(t('signup.profileName'));
    if (!formData.country) missing.push(t('signup.country'));
    if (!formData.mainCity.trim()) missing.push(t('signup.mainCity'));
    if (!formData.services.length) missing.push(t('signup.validation.atLeastOneService'));
    if (!formData.profilePhoto) missing.push(t('signup.profilePhoto'));
    if (formData.galleryPhotos.length < 3) missing.push(t('signup.validation.atLeastThreePhotos'));
    
    // Vérifier qu'au moins une info de contact est remplie
    const hasContactInfo = formData.instagram.trim() || 
                          formData.whatsapp.trim() || 
                          formData.phone?.trim() || 
                          formData.tiktok.trim() || 
                          formData.externalLink.trim();
    if (!hasContactInfo) missing.push(t('signup.validation.atLeastOneContact'));
    
      // Si aucun réseau primaire n'est sélectionné, sélectionner automatiquement le premier réseau renseigné
      if (!formData.primaryContact?.trim() && hasContactInfo) {
        const firstFilledContact = contactFields.find(
          (field) => formData[field.key]?.trim()
        );
      if (firstFilledContact) {
        setFormData((prev) => ({ ...prev, primaryContact: firstFilledContact.key }));
        // Ne pas ajouter d'erreur car on vient de le corriger automatiquement
      }
    } else if (!formData.primaryContact?.trim()) {
      missing.push(t('signup.validation.primaryContactRequired'));
    }

    if (missing.length) {
      setFeedback({
        type: 'error',
        message: t('signup.validation.pleaseCompleteFields', { fields: missing.join(', ') }),
      });
      // Scroll vers le message d'erreur sur mobile
      setTimeout(() => {
        const feedbackElement = document.querySelector(`.${styles.feedback}`);
        if (feedbackElement) {
          feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

      // Stocker les données du profil temporairement
      // Les fichiers sont déjà en base64 dans profilePhoto et galleryPhotos
      const tempProfileData = {
        formData: { ...formData },
        accountData: null,
        requiresAccountCreation,
      };
      sessionStorage.setItem('tempHairstylistProfile', JSON.stringify(tempProfileData));

      if (!requiresAccountCreation) {
        // Utilisateur déjà connecté : créer le profil puis passer au paiement
        setSubmitting(true);
        try {
          // Créer le profil pour l'utilisateur existant
          const profileId = await createProfileForExistingUser(formData, currentUser);
          
          // Mettre à jour le userType à "service"
          await updateUserType(currentUser.uid);
          
          // Stocker l'ID du profil créé dans le Context
          setProfileId(profileId);
          
          // Naviguer vers la page de paiement
          navigate('/signup/payment');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating profile for existing user:', error);
          setFeedback({
            type: 'error',
            message: t('signup.errorProfileCreation'),
          });
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // Passer à l'étape 2 : création de compte
      setStep(2);
      navigate(`/signup/${providerType}?step=2`);
      window.scrollTo(0, 0);
      return;
    }

    // Étape 2 : Validation et création du compte + profil
    if (step === 2) {
      // 0. Vérifier que les données du profil (étape 1) sont bien présentes (évite profils vides si accès direct à ?step=2)
      const hasProfileData = formData.name?.trim()
        && formData.country
        && formData.mainCity?.trim()
        && Array.isArray(formData.services) && formData.services.length > 0
        && formData.profilePhoto
        && Array.isArray(formData.galleryPhotos) && formData.galleryPhotos.length >= 3
        && (formData.instagram?.trim() || formData.whatsapp?.trim() || formData.phone?.trim() || formData.tiktok?.trim() || formData.externalLink?.trim());
      if (!hasProfileData) {
        setFeedback({
          type: 'error',
          message: t('signup.validation.completeProfileFirst'),
        });
        setStep(1);
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // ÉTAPE 2 : VALIDATION DES DONNÉES DU COMPTE
      // 1. Vérifier que tous les champs obligatoires sont remplis
      const missing = [];
      if (!accountData.displayName.trim()) missing.push(t('signup.username'));
      if (!accountData.email.trim()) missing.push(t('signup.email'));
      if (!accountData.password) missing.push(t('signup.password'));
      if (!accountData.confirmPassword) missing.push(t('signup.validation.confirmPasswordLabel'));

      if (missing.length) {
        setFeedback({
          type: 'error',
          message: t('signup.validation.pleaseCompleteFields', { fields: missing.join(', ') }),
        });
        // Scroll vers le message d'erreur sur mobile
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // 2. Validation du format du pseudo
      if (accountData.displayName.trim()) {
        const pseudoRegex = /^[a-zA-Z0-9._-]+$/;
        if (!pseudoRegex.test(accountData.displayName.trim())) {
          setFeedback({
            type: 'error',
            message: t('auth.usernameInvalid'),
          });
          // Scroll vers le message d'erreur sur mobile
          setTimeout(() => {
            const feedbackElement = document.querySelector(`.${styles.feedback}`);
            if (feedbackElement) {
              feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      }

      // 3. Validation du format de l'email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(accountData.email.trim())) {
        setFeedback({
          type: 'error',
          message: t('auth.emailInvalid'),
        });
        // Scroll vers le message d'erreur sur mobile
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // 4. Validation de la longueur du mot de passe
      if (accountData.password.length < 6) {
        setFeedback({
          type: 'error',
          message: t('auth.passwordMinLength'),
        });
        // Scroll vers le message d'erreur sur mobile
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // 5. Validation de la correspondance des mots de passe
      if (accountData.password !== accountData.confirmPassword) {
        setFeedback({
          type: 'error',
          message: t('auth.passwordsDontMatch'),
        });
        // Scroll vers le message d'erreur sur mobile
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      return;
    }

    // Bloquer l'interface dès le début du traitement
    setSubmitting(true);
    setFeedback({ type: 'info', message: t('signup.creatingAccount') });
    
    try {
        // Vérifier l'unicité du pseudo
        const usersQuery = query(
          collection(db, 'users'),
          where('displayName', '==', accountData.displayName.trim())
        );
        const usersSnapshot = await getDocs(usersQuery);
        if (!usersSnapshot.empty) {
          setFeedback({
            type: 'error',
            message: t('auth.usernameTaken'),
          });
          // Scroll vers le message d'erreur sur mobile
          setTimeout(() => {
            const feedbackElement = document.querySelector(`.${styles.feedback}`);
            if (feedbackElement) {
              feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          setSubmitting(false);
          return;
        }

        // Vérifier aussi l'unicité de l'email (vérifier dans Firebase Auth d'abord)
        try {
          const signInMethods = await fetchSignInMethodsForEmail(auth, accountData.email.trim().toLowerCase());
          if (signInMethods && signInMethods.length > 0) {
            setFeedback({
              type: 'error',
              message: t('auth.emailTaken'),
            });
            // Scroll vers le message d'erreur sur mobile
            setTimeout(() => {
              const feedbackElement = document.querySelector(`.${styles.feedback}`);
              if (feedbackElement) {
                feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            setSubmitting(false);
            return;
          }
        } catch (error) {
          // Si la vérification échoue, bloquer le passage à l'étape 3 pour des raisons de sécurité
          // eslint-disable-next-line no-console
          console.error('Error checking email in Auth:', error);
          setFeedback({
            type: 'error',
            message: t('signup.errorEmailVerification'),
          });
          // Scroll vers le message d'erreur sur mobile
          setTimeout(() => {
            const feedbackElement = document.querySelector(`.${styles.feedback}`);
            if (feedbackElement) {
              feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          setSubmitting(false);
          return;
        }

        // Vérifier aussi dans Firestore si un document users existe déjà avec cet email
        const emailQuery = query(
          collection(db, 'users'),
          where('email', '==', accountData.email.trim().toLowerCase())
        );
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) {
          setFeedback({
            type: 'error',
            message: t('auth.emailTaken'),
          });
          // Scroll vers le message d'erreur sur mobile
          setTimeout(() => {
            const feedbackElement = document.querySelector(`.${styles.feedback}`);
            if (feedbackElement) {
              feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          setSubmitting(false);
          return;
        }

        // Stocker les données du compte dans le Context React
        setSignupAccountData({ ...accountData });
        
        // NOUVEAU FLOW: Créer le compte et le profil FREE immédiatement
        // eslint-disable-next-line no-console
        
        try {
          // 1. Créer le compte utilisateur
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            accountData.email.trim(),
            accountData.password
          );
          const user = userCredential.user;
          
          // eslint-disable-next-line no-console

          // 2. Mettre à jour le profil utilisateur avec le displayName
          await updateProfile(user, {
            displayName: accountData.displayName.trim(),
          });

          // 3. Générer le code d'identification
          const identificationCode = await generateUniqueIdentificationCode();

          // 4. Upload de la photo de profil
          let profilePhotoURL = '';
          if (formData.profilePhoto && formData.profilePhoto.startsWith('data:')) {
            const response = await fetch(formData.profilePhoto);
            const blob = await response.blob();
            const timestamp = Date.now();
            const profilePhotoRef = ref(
              storage,
              `hairstylists/${formData.name.trim().replace(/\s+/g, '_')}_${timestamp}/profile.jpg`
            );
            await uploadBytes(profilePhotoRef, blob);
            profilePhotoURL = await getDownloadURL(profilePhotoRef);
          }

          // 5. Upload des photos de galerie (redimensionnées pour optimiser le chargement)
          const galleryPhotoURLs = [];
          if (formData.galleryPhotos && formData.galleryPhotos.length > 0) {
            const timestamp = Date.now();
            const uploadPromises = formData.galleryPhotos
              .filter(photo => photo && photo.startsWith('data:'))
              .map(async (photoDataUrl, index) => {
                try {
                  // Redimensionner l'image à 400x400px max pour optimiser le chargement dans les vignettes
                  const resizedDataUrl = await resizeImageFromDataUrl(photoDataUrl, 400, 400, 0.85);
                  const response = await fetch(resizedDataUrl);
                  const blob = await response.blob();
                  const galleryPhotoRef = ref(
                    storage,
                    `hairstylists/${formData.name.trim().replace(/\s+/g, '_')}_${timestamp}/gallery_${index}.jpg`
                  );
                  await uploadBytes(galleryPhotoRef, blob);
                  return getDownloadURL(galleryPhotoRef);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error(`Erreur upload photo galerie ${index}:`, error);
                  return null;
                }
              });
            const urls = await Promise.all(uploadPromises);
            galleryPhotoURLs.push(...urls.filter(Boolean));
          }

          // 6. Vérifier les services enfants
          const kidsServiceCodeSet = new Set(['ENF001', 'ENF002', 'ENF003', 'ENF004', 'ENF005']);
          const kidsAccepted = hasKidsServicesFromCodes(formData.services, kidsServiceCodeSet);

          // Normaliser les numéros de téléphone / WhatsApp (indicatif + chiffres, sans 0 initial)
          const buildPhoneValue = (countryCode, number) => {
            const raw = (number || '').trim();
            if (!raw) return '';
            // Si l'utilisateur a déjà mis un +, garder tel quel (sans espaces)
            if (raw.startsWith('+')) {
              return raw.replace(/\s+/g, '');
            }
            let digits = raw.replace(/[^0-9]/g, '');
            // Supprimer un 0 initial (ex: 0470... -> 470...) car l'indicatif remplace le 0
            if (digits.startsWith('0')) {
              digits = digits.slice(1);
            }
            const code = (countryCode || '+32').trim();
            return `${code}${digits}`;
          };

          const normalizedWhatsapp = buildPhoneValue(formData.whatsappCountryCode, formData.whatsapp);
          const normalizedPhone = buildPhoneValue(formData.phoneCountryCode, formData.phone);

          // [SIGNUP DEBUG] formData au moment de la création du profil
          // eslint-disable-next-line no-console
          console.log('[SignupPage] formData (avant payload)', {
            country: formData.country,
            city: formData.city,
            mainCity: formData.mainCity,
            department: formData.department,
          });

          // 7. Créer le profil hairstylist FREE
          const payload = {
            name: formData.name.trim(),
            mainCity: formData.mainCity.trim(),
            cities: [formData.mainCity.trim(), ...(formData.additionalCities || [])].filter(Boolean),
            country: formData.country || null,
            city: formData.city?.trim() || formData.mainCity.trim() || null,
            department: formData.department ?? null,
            description: formData.description.trim(),
            canTravel: formData.canTravel,
            kidsAccepted,
            services: formData.services,
            profilePhoto: profilePhotoURL,
            galleryPhotos: galleryPhotoURLs,
            contact: {
              instagram: formData.instagram?.trim() ? `@${formData.instagram.trim().replace(/^@/, '')}` : '',
              whatsapp: normalizedWhatsapp,
              phone: normalizedPhone,
              tiktok: formData.tiktok?.trim() ? `@${formData.tiktok.trim().replace(/^@/, '')}` : '',
              externalLink: formData.externalLink?.trim() || '',
              primaryContact: formData.primaryContact?.trim() || '',
            },
            instagram: formData.instagram?.trim() ? `@${formData.instagram.trim().replace(/^@/, '')}` : '',
            whatsapp: normalizedWhatsapp,
            phone: normalizedPhone,
            tiktok: formData.tiktok?.trim() ? `@${formData.tiktok.trim().replace(/^@/, '')}` : '',
            externalLink: formData.externalLink?.trim() || '',
            primaryContact: formData.primaryContact?.trim() || '',
            coordinates: formData.coordinates,
            identificationCode,
            userId: user.uid,
            userEmail: user.email,
            subscriptionActive: false,
            subscriptionStartDate: null,
            accountType: 'FREE',
            paymentStatus: formData.country && formData.country !== 'BE' ? 'pending' : 'ok',
            type: providerType, // Type de prestataire
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          // [SIGNUP DEBUG] Payload envoyé à Firestore (hairstylists)
          // eslint-disable-next-line no-console
          console.log('[SignupPage] payload Firestore (hairstylists)', {
            country: payload.country,
            city: payload.city,
            mainCity: payload.mainCity,
            department: payload.department,
          });

          const docRef = await addDoc(collection(db, 'hairstylists'), payload);

          // 8. Créer le document users
          await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            displayName: accountData.displayName.trim(),
            email: user.email,
            createdAt: serverTimestamp(),
            accountType: 'FREE',
            userType: 'service', // User has a hairstylist profile, so they are a service provider
          });

          // 9. Envoyer l'email de vérification (ne pas bloquer l'inscription si échec)
          try {
            await sendEmailVerification(user);
          } catch (emailErr) {
            // eslint-disable-next-line no-console
            console.warn('[SignupPage] Envoi email de vérification échoué (compte et profil déjà créés):', emailErr);
          }

          // 10. Stocker l'ID du profil créé dans le Context
          setProfileId(docRef.id);

          // 10b. Créer une conversation chat et envoyer un message de bienvenue
          try {
            const conversationsRef = collection(db, 'chatConversations');
            const newConv = await addDoc(conversationsRef, {
              userId: user.uid,
              userDisplayName: accountData.displayName.trim(),
              userEmail: user.email,
              status: 'pending',
              unreadCount: 1,
              lastMessageSender: 'admin',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            const messagesRef = collection(db, 'chatConversations', newConv.id, 'messages');
            await addDoc(messagesRef, {
              text: t('signup.chatWelcomeMessage'),
              sender: 'admin',
              adminName: 'Chérie Coiffer',
              createdAt: serverTimestamp(),
              read: false,
            });
          } catch (chatErr) {
            // eslint-disable-next-line no-console
            console.warn('[SignupPage] Message de bienvenue chat non envoyé (inscription réussie):', chatErr);
          }

          // 11. Navigation IMMÉDIATE vers la page de paiement
          setIsNavigating(true);
          navigate('/signup/payment');
        } catch (createError) {
          // eslint-disable-next-line no-console
          console.error('[SignupPage] Erreur lors de la création du compte/profil:', createError);
          
          // Logger l'erreur critique pour monitoring
          await logSignupError(createError, {
            email: accountData?.email,
            displayName: accountData?.displayName,
            step: 'account_and_profile_creation',
            formDataName: formData?.name,
          });
          
          let errorMessage = 'Erreur lors de la création du compte. ';
          if (createError.code === 'auth/email-already-in-use') {
            errorMessage = 'Cette adresse email est déjà utilisée.';
          } else if (createError.code === 'auth/weak-password') {
            errorMessage = 'Le mot de passe est trop faible.';
          } else if (createError.code === 'auth/invalid-email') {
            errorMessage = 'L\'adresse email n\'est pas valide.';
          } else if (
            createError.code === 'auth/network-request-failed' ||
            createError.code === -26 ||
            createError.code === '-26' ||
            (createError.message && (createError.message.includes('network') || createError.message.includes('-26')))
          ) {
            errorMessage = 'Problème de connexion. Vérifiez votre connexion internet (Wi‑Fi ou données) et réessayez.';
          } else if (createError.message) {
            errorMessage += createError.message;
          }
          
          setFeedback({
            type: 'error',
            message: errorMessage,
          });
          setTimeout(() => {
            const feedbackElement = document.querySelector(`.${styles.feedback}`);
            if (feedbackElement) {
              feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          setSubmitting(false);
        }
      } catch (error) {
        setFeedback({
          type: 'error',
          message: t('signup.errorGeneric'),
        });
        // Scroll vers le message d'erreur sur mobile
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        // eslint-disable-next-line no-console
        console.error('Failed to validate account data', error);
        setSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('signup.confirmCancel'))) {
      navigate('/');
    }
  };

  const handleNext = async (e) => {
    // Empêcher les doubles clics
    if (submitting) {
      return;
    }
    
    // Pour l'étape 1, faire la validation directement ici (comme handleCancel est simple)
    if (step === 1) {
      // Validation directe
      const missing = [];
      
      if (!formData.name?.trim()) missing.push(t('signup.profileName'));
      if (!formData.country) missing.push(t('signup.country'));
      if (!formData.mainCity?.trim()) missing.push(t('signup.mainCity'));
      if (!formData.services?.length) missing.push(t('signup.validation.atLeastOneService'));
      if (!formData.profilePhoto) missing.push(t('signup.profilePhoto'));
      if (formData.galleryPhotos?.length < 3) missing.push(t('signup.validation.atLeastThreePhotos'));
      
      const hasContactInfo = formData.instagram?.trim() || 
                            formData.whatsapp?.trim() || 
                            formData.phone?.trim() || 
                            formData.tiktok?.trim() || 
                            formData.externalLink?.trim();
      if (!hasContactInfo) missing.push(t('signup.validation.atLeastOneContact'));

      if (missing.length) {
        setFeedback({
          type: 'error',
          message: t('signup.validation.pleaseCompleteFields', { fields: missing.join(', ') }),
        });
        setTimeout(() => {
          const feedbackElement = document.querySelector(`.${styles.feedback}`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // Validation OK - stocker dans Context React (images incluses) et naviguer
      // Nettoyer formData pour enlever les File objects (non sérialisables)
      const cleanFormData = {
        ...formData,
        profilePhotoFile: null, // Ne pas stocker le File
        galleryPhotoFiles: [], // Ne pas stocker les Files
      };
      
      // Stocker dans le Context React (inclut les images base64 - pas de problème de quota !)
      setSignupFormData(cleanFormData);
      setSignupRequiresAccountCreation(requiresAccountCreation);
      
      // Stocker uniquement les métadonnées dans sessionStorage (pour la restauration si refresh)
      const metadataOnly = {
        name: formData.name,
        mainCity: formData.mainCity,
        services: formData.services,
        additionalCities: formData.additionalCities,
        canTravel: formData.canTravel,
        instagram: formData.instagram,
        whatsapp: formData.whatsapp,
        tiktok: formData.tiktok,
        externalLink: formData.externalLink,
        description: formData.description,
        coordinates: formData.coordinates,
        // Pas d'images dans sessionStorage
      };
      
      try {
        sessionStorage.setItem('tempHairstylistProfile', JSON.stringify({
          formData: metadataOnly,
          accountData: null,
          requiresAccountCreation,
          imagesInContext: true, // Flag pour indiquer que les images sont dans le Context
        }));
      } catch (error) {
        // Non bloquant, les images sont dans le Context
      }

      if (!requiresAccountCreation) {
        // Utilisateur déjà connecté : créer le profil puis passer au paiement
        setSubmitting(true);
        try {
          // Créer le profil pour l'utilisateur existant
          const profileId = await createProfileForExistingUser(formData, currentUser);
          
          // Mettre à jour le userType à "service"
          await updateUserType(currentUser.uid);
          
          // Stocker l'ID du profil créé dans le Context
          setProfileId(profileId);
          
          // Naviguer vers la page de paiement
          navigate('/signup/payment');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating profile for existing user:', error);
          setFeedback({
            type: 'error',
            message: t('signup.errorProfileCreation'),
          });
        } finally {
          setSubmitting(false);
        }
        return;
      }

      setStep(2);
      navigate(`/signup/${providerType}?step=2`);
      window.scrollTo(0, 0);
      return;
    }
    
    // Pour l'étape 2, utiliser handleSubmit normalement
    const form = formRef.current;
    if (!form) {
      setFeedback({
        type: 'error',
        message: t('signup.errorFormNotFound'),
      });
      return;
    }
    
    // Créer un événement mock pour handleSubmit
    const mockEvent = {
      preventDefault: () => {},
      target: form,
      currentTarget: form,
    };
    
    try {
      await handleSubmit(mockEvent);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: t('signup.errorOccurred'),
      });
    }
  };

  const handlePrevious = () => {
    if (showAccountStep) {
      setStep(1);
      navigate(`/signup/${providerType}`);
    }
  };

  // Si on est en train de naviguer, afficher un écran de transition
  if (isNavigating) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div style={{ textAlign: 'center', padding: '100px 20px', color: '#333' }}>
            <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>✅ Compte créé avec succès !</h2>
            <p style={{ fontSize: '18px' }}>Redirection vers la page de paiement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        {alreadyHairstylist ? (
          <div className={styles.alreadyHairstylistBox}>
            <h1>{t('signup.alreadyHairstylistTitle')}</h1>
            <p>
              {t('signup.alreadyHairstylistMessage')}
            </p>
            <button
              type="button"
              className={styles.submit}
              onClick={() => {
                if (existingHairstylistId) {
                  navigate(`/hairstylist/${existingHairstylistId}`);
                } else {
                  navigate(-1);
                }
              }}
            >
              {t('signup.viewMyProfile')}
        </button>
          </div>
        ) : (
          <>
            {/* Barre de progression */}
            <ProgressBar currentStep={showAccountStep ? 2 : 1} providerType={providerType} />

            {!showAccountStep ? (
              <>
                <div className={styles.contentSection}>
                  <h2 className={styles.contentTitle}>{t('signup.welcomeTitle')}</h2>
        <p className={styles.subtitle}>
          {t('signup.welcomeSubtitle')}
        </p>

                  {/* Encadré vert */}
                  <div className={styles.infoBox}>
                    <p>{t('signup.infoBox')}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.contentSection}>
                  <h2 className={styles.contentTitle}>{t('signup.createAccountTitle')}</h2>
                  <p className={styles.subtitle}>
                    {t('signup.createAccountSubtitle')}
                  </p>
                </div>
              </>
            )}

        <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
          {!showAccountStep ? (
            // Étape 1 : Formulaire profil (tout le contenu existant)
            <>
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>1.</span>
              <div>
                <h2>{t('signup.section1Title')}</h2>
                <p>{t('signup.section1Subtitle')}</p>
              </div>
            </div>

            <div className={styles.fieldGrid}>
              <label>
                {t('signup.profileName')}
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder={t('signup.profileNamePlaceholder')}
                  required
                />
              </label>
              <label>
                {t('signup.country')}
                <SearchableCountrySelect
                  value={formData.country || ''}
                  onChange={(newCode) => {
                    setFormData((prev) => ({ ...prev, country: newCode || null }));
                    if (mainCityInputRef.current) mainCityInputRef.current.value = '';
                  }}
                  locale={language || 'fr'}
                  placeholder={t('signup.selectCountry')}
                  noResultsLabel={t('signup.noCountryFound')}
                  allowEmpty={false}
                />
              </label>
              <label>
                {t('signup.mainCity')}
                <input
                  type="text"
                  ref={mainCityInputRef}
                  value={formData.mainCity}
                  onChange={handleInputChange('mainCity')}
                  placeholder={t('signup.mainCityPlaceholder')}
                  required
                />
              </label>
            </div>

            <div className={styles.locationRow}>
              <div className={styles.travelToggle}>
                <span>{t('signup.canTravel')}</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={formData.canTravel}
                    onChange={handleCheckboxChange('canTravel')}
                  />
                  <span className={styles.slider} />
                </label>
              </div>
              {formData.canTravel && (
                <div className={styles.addressSearch}>
                  <input
                    type="text"
                    ref={locationInputRef}
                    placeholder={t('signup.addCity')}
                    onKeyDown={handleAdditionalCityKeyDown}
                  />
                </div>
              )}
            </div>
            {formData.canTravel && formData.additionalCities.length > 0 && (
              <div className={styles.cityChips}>
                {formData.additionalCities.map((city) => (
                  <span key={city} className={styles.cityChip}>
                    {city}
                    <button type="button" onClick={() => handleRemoveAdditionalCity(city)}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}


            <label>
              {t('signup.presentation')}
              <textarea
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder={t('signup.presentationPlaceholder')}
              />
            </label>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>2.</span>
              <div>
                <h2>{t('signup.section2Title')}</h2>
                <p>{t('signup.section2Subtitle')}</p>
              </div>
            </div>

            <div className={styles.contactsBox}>
              {contactFields.map((field) => (
                <label key={field.key} className={styles.contactField}>
                  <span className={styles.contactLabel}>
                    <img src={field.icon} alt={field.label} className={field.key === 'tiktok' ? styles.contactIconTiktok : undefined} />
                    {field.label}
                  </span>
                  {(field.key === 'whatsapp' || field.key === 'phone') ? (
                    <div className={styles.phoneInputRow}>
                      <CountryCodeSelect
                        value={field.key === 'whatsapp' ? formData.whatsappCountryCode : formData.phoneCountryCode}
                        onChange={handleCountryCodeChange(
                          field.key === 'whatsapp' ? 'whatsappCountryCode' : 'phoneCountryCode',
                        )}
                      />
                      <input
                        type={field.type}
                        value={formData[field.key]}
                        onChange={handleInputChange(field.key)}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.key]}
                      onChange={handleInputChange(field.key)}
                      placeholder={field.placeholder}
                    />
                  )}
                </label>
              ))}
            </div>

            <div className={styles.primaryContactSection}>
              <div className={styles.primaryContactRow}>
                <label className={styles.primaryContactLabel}>
                  {t('signup.primaryContact')}
                </label>
                <select
                  value={formData.primaryContact}
                  onChange={handleInputChange('primaryContact')}
                  className={styles.primaryContactSelect}
                  required
                >
                  <option value="">{t('signup.selectPrimary')}</option>
                  {contactFields.map((field) => (
                    formData[field.key]?.trim() && (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    )
                  ))}
                </select>
              </div>
              <p className={styles.helpText}>
                {t('signup.primaryContactHelp')}
              </p>
            </div>
          </section>

  <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>3.</span>
              <div>
                <h2>{t('signup.section3Title')}</h2>
                <p>{t('signup.section3Subtitle')}</p>
              </div>
            </div>

            <div className={styles.photoRow}>
              <div className={styles.photoUpload}>
                {formData.profilePhoto ? (
                  <>
                    <img src={formData.profilePhoto} alt="Profil" />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          profilePhoto: '',
                          profilePhotoFile: null,
                        }))
                      }
                    >
                      {t('signup.remove')}
                    </button>
                  </>
                ) : (
                  <>
                    <span>{t('signup.profilePhoto')}</span>
                    <label className={styles.uploadBtn}>
                      {t('signup.addPhoto')}
                      <input type="file" accept="image/*" onChange={handleProfilePhotoUpload} />
                    </label>
                  </>
                )}
              </div>

              <div className={styles.galleryUpload}>
                <div className={styles.galleryList}>
                  {formData.galleryPhotos.map((photo, index) => (
                    <div key={`gallery-${index}-${photo.substring(0, 50)}`} className={styles.galleryItem}>
                      <img src={photo} alt={`Galerie ${index + 1}`} />
                      <button type="button" onClick={() => handleGalleryRemove(index)}>
                        {t('signup.remove')}
                      </button>
                    </div>
                  ))}
                  {formData.galleryPhotos.length < 6 && (
                    <label className={styles.galleryAdd}>
                      +
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleGalleryUpload}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>4.</span>
              <div>
                <h2>{t('signup.section4Title')}</h2>
                <p>{t('signup.section4Subtitle')}</p>
              </div>
            </div>

            {loadingCatalog && <p>{t('signup.loadingCategories')}</p>}
            {!loadingCatalog &&
              servicesByCategory.map((category) => {
                const categoryServiceCodes = category.services.map(s => s.code);
                const allSelected = categoryServiceCodes.length > 0 && 
                  categoryServiceCodes.every(code => formData.services.includes(code));
                const someSelected = categoryServiceCodes.some(code => formData.services.includes(code));

                return (
                <div key={category.categoryCode} className={styles.accordion}>
                  <button
                    type="button"
                    className={styles.accordionHeader}
                    onClick={() => toggleCategory(category.categoryCode)}
                  >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="checkbox"
                          className={styles.categoryCheckbox}
                          checked={allSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someSelected && !allSelected;
                            }
                          }}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCategoryToggle(category.categoryCode);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                    <span>{category.label}</span>
                      </div>
                    <span>{expanded[category.categoryCode] ? '▴' : '▾'}</span>
                  </button>
                  {expanded[category.categoryCode] && (
                    <div className={styles.serviceGrid}>
                      {category.services.map((service) => (
                        <label key={service.code}>
                          <input
                            type="checkbox"
                            checked={formData.services.includes(service.code)}
                            onChange={() => handleServiceToggle(service.code)}
                          />
                          <span>{service.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
          </section>

          {/* Messages de validation au-dessus des boutons */}
          {feedback && (
            <div className={`${styles.feedback} ${styles[feedback.type]}`}>
              {feedback.message}
            </div>
          )}

          {/* Boutons en bas de la page */}
          <div className={styles.bottomActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
            >
              {t('signup.cancel')}
          </button>
            <button
              type="button"
              className={styles.nextButton}
              onClick={handleNext}
              disabled={submitting}
            >
              {t('signup.next')}
            </button>
          </div>

          {/* Bouton submit caché - le bouton Suivant déclenche la soumission */}
          <button type="submit" className={styles.hiddenSubmit} disabled={submitting} aria-hidden="true" tabIndex={-1}>
            {submitting
              ? t('signup.submitting')
              : requiresAccountCreation
                ? t('signup.continue')
                : t('signup.validateProfile')}
          </button>
          </>
          ) : (
            // Étape 2 : Création de compte
            <>
              <section className={styles.card}>
                <div className={styles.sectionHeader}>
                  <span>2.</span>
                  <div>
                    <h2>{t('signup.createAccountTitle')}</h2>
                    <p>{t('signup.createAccountSubtitle2')}</p>
                  </div>
                </div>

                <div className={styles.fieldGrid}>
                  {/* Champs pièges pour capturer l'autocomplete Google Maps */}
                  <div className={styles.hiddenGoogleTrap} aria-hidden="true">
                    <input
                      type="text"
                      name="address-trap"
                      id="address-trap"
                      tabIndex={-1}
                      autoComplete="address-line1"
                      placeholder="Address"
                    />
                    <input
                      type="text"
                      name="city-trap"
                      id="city-trap"
                      tabIndex={-1}
                      autoComplete="address-level2"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      name="location-trap"
                      id="location-trap"
                      tabIndex={-1}
                      autoComplete="street-address"
                      placeholder="Location"
                      className="pac-target-input"
                    />
                  </div>
                  
                  <label className={styles.fullWidth}>
                    {t('signup.username')}
                    <input
                      type="text"
                      name="signup-username"
                      value={accountData.displayName}
                      onChange={(e) => setAccountData({...accountData, displayName: e.target.value.toLowerCase()})}
                      placeholder={t('signup.usernamePlaceholder')}
                      required
                      autoComplete="username"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </label>
                  
                  <label className={styles.fullWidth}>
                    {t('signup.email')}
                    <input
                      type="email"
                      name="signup-email"
                      value={accountData.email}
                      onChange={(e) => setAccountData({ ...accountData, email: e.target.value.toLowerCase() })}
                      placeholder={t('signup.emailPlaceholder')}
                      required
                      autoComplete="email"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </label>
                  
                  <label>
                    {t('signup.password')}
                    <input
                      type="password"
                      name="signup-password"
                      value={accountData.password}
                      onChange={(e) => setAccountData({...accountData, password: e.target.value})}
                      placeholder={t('signup.passwordPlaceholder')}
                      minLength={6}
                      required
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </label>
                  
                  <label>
                    {t('signup.confirmPassword')}
                    <input
                      type="password"
                      name="signup-confirm-password"
                      value={accountData.confirmPassword}
                      onChange={(e) => setAccountData({...accountData, confirmPassword: e.target.value})}
                      placeholder={t('signup.confirmPasswordPlaceholder')}
                      minLength={6}
                      required
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </label>
                </div>
              </section>

              {/* Messages de validation au-dessus des boutons */}
              {feedback && (
                <div className={`${styles.feedback} ${styles[feedback.type]}`}>
                  {feedback.message}
                </div>
              )}

              {/* Boutons Précédent et Suivant en bas de la page */}
              <div className={styles.bottomActions}>
                <button
                  type="button"
                  className={styles.previousButton}
                  onClick={handlePrevious}
                >
                  {t('signup.previous')}
                </button>
                <button
                  type="button"
                  className={styles.nextButton}
                  onClick={handleNext}
                  disabled={submitting}
                >
                  {t('signup.next')}
                </button>
              </div>

              {/* Bouton submit caché - le bouton Suivant déclenche la soumission */}
              <button type="submit" className={styles.hiddenSubmit} disabled={submitting} aria-hidden="true" tabIndex={-1}>
                {submitting ? t('signup.creating') : t('signup.createAccountAndJoin')}
              </button>
            </>
          )}
        </form>
          </>
        )}
      </div>
    </div>
  );
}

export default SignupPage;


